import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';

import { formatPolicyAmount } from '../config/expensePolicyHelpers';

// Limit aşımı talebi bloklamaz; bu kutu kararı görünür kılar. `entry` giriş
// ekranında (talep gönderilmeden önce), `saved` kayıtlı talebin detayında.
const Box = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  background: rgba(229, 57, 53, 0.08);
  border: 1px solid rgba(229, 57, 53, 0.25);
  color: #b71c1c;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Title = styled.strong`
  font-weight: 700;
`;

const Line = styled.span`
  font-weight: 500;
`;

const Strong = styled.span`
  font-weight: 700;
`;

export default function ExpensePolicyNotice({ policy, variant = 'entry', className, style }) {
  const { t } = useTranslation();

  const lines = useMemo(() => {
    if (!policy) return [];
    const currency = policy.currency || 'TRY';
    const amount = (value) => formatPolicyAmount(value, currency);
    const out = [];

    if (variant === 'entry') {
      if (policy.perTransactionLimit != null) {
        out.push({ key: 'limit', text: t('expense.policy.perTransactionLine', { limit: amount(policy.perTransactionLimit) }) });
      }
      if (policy.monthlyEmployeeLimit != null) {
        out.push({
          key: 'monthly',
          text: t('expense.policy.monthlyLine', {
            limit: amount(policy.monthlyEmployeeLimit),
            consumed: amount(policy.monthlyConsumed),
          }),
        });
      }
      out.push({ key: 'entered', text: t('expense.policy.enteredAmount', { amount: amount(policy.totalAmount) }) });
      out.push({ key: 'payable', strong: true, text: t('expense.policy.payableAmount', { amount: amount(policy.payableAmount) }) });
      out.push({ key: 'exceeded', text: t('expense.policy.notPayable', { amount: amount(policy.exceededAmount) }) });
      if (policy.isFallback) out.push({ key: 'fallback', text: t('expense.policy.fallbackNote') });
      return out;
    }

    if (policy.limitAmount != null) {
      out.push({ key: 'limit', text: t('expense.policy.perTransactionLine', { limit: amount(policy.limitAmount) }) });
    }
    if (policy.monthlyLimitAmount != null) {
      out.push({ key: 'monthlyLimit', text: t('expense.policy.monthlyLimitHint', { limit: amount(policy.monthlyLimitAmount) }) });
    }
    if (policy.showsPayable) {
      out.push({ key: 'payable', strong: true, text: t('expense.policy.payableAmount', { amount: amount(policy.payableAmount) }) });
      out.push({ key: 'exceeded', text: t('expense.policy.notPayable', { amount: amount(policy.exceededAmount) }) });
    }
    return out;
  }, [policy, variant, t]);

  if (!policy) return null;

  const title = variant === 'entry'
    ? t('expense.policy.entryTitle')
    : (policy.showsPayable ? t('expense.policy.savedTitle') : t('expense.policy.badge'));

  return (
    <Box className={className} style={style} data-testid="expense-policy-notice">
      <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
      <Body>
        <Title>{title}</Title>
        {lines.map(({ key, text, strong }) => (
          strong ? <Strong key={key}>{text}</Strong> : <Line key={key}>{text}</Line>
        ))}
      </Body>
    </Box>
  );
}
